/**
 * Which background images the first banner slide should preload, and at what widths.
 *
 * WHY THIS IS A SEPARATE, TESTED MODULE. The banner <img> is the LCP element on 88-89% of
 * real loads, but it is a raw <picture> rather than next/image, so Next emits no preload for
 * it and the browser only discovers it when the parser reaches the tag — ~34 KB into the
 * document. A preload hint fixes that, but ONLY if its media query matches the <source>
 * query exactly: disagree by one pixel and the browser preloads a variant it never uses,
 * turning a saving into pure waste. That invariant is what the tests here pin down.
 *
 * A preload inserts no element and changes no box, so none of this can cause a layout shift.
 */
export interface BannerSlideImages {
  desktop_bg_image?: string;
  mobile_bg_image?: string;
}

export interface BannerPreload {
  href: string;
  /** Undefined means "used at every width", so the hint must not be scoped. */
  media: string | undefined;
}

export function bannerPreloads(
  item: BannerSlideImages,
  desktopMinPx: number,
): BannerPreload[] {
  const desktop = item.desktop_bg_image;
  const mobile = item.mobile_bg_image;

  if (desktop && mobile) {
    return [
      { href: desktop, media: `(min-width: ${desktopMinPx}px)` },
      { href: mobile, media: `(max-width: ${desktopMinPx - 1}px)` },
    ];
  }

  // With one image set both <source> tags collapse and the <img src> fallback
  // (`mobile || desktop`) is what renders, at every width — so do not scope the hint.
  const only = mobile || desktop;
  return only ? [{ href: only, media: undefined }] : [];
}
